import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { isDefined } from '../typeUtils/index.js';
/** Default country set when callers don't specify one — preserves the historical US-only behavior. */
export const DEFAULT_ALLOWED_PHONE_NUMBER_COUNTRIES = ['US'];
/**
 * Country set used for outbound voice and SMS (callee, transfer destinations, SMS recipient).
 * Centralized so widening the scope (e.g. add UK) is a one-line change.
 */
export const OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES = ['US', 'CA'];
const COUNTRY_CODE_DISPLAY_NAMES = {
    US: 'US',
    CA: 'Canadian',
};
/**
 * Builds an "Invalid …" label suitable for both Zod messages (caller appends `.`) and runtime
 * error messages (caller appends `: <number>`). Single country: `Invalid US phone number`.
 * Multiple: `Invalid phone number (must be a valid US, Canadian, or GB phone number)`.
 */
export function buildInvalidPhoneNumberLabel(allowedCountries) {
    const label = joinCountryDisplayNames(allowedCountries);
    if (allowedCountries.length === 1) {
        return `Invalid ${label} phone number`;
    }
    return `Invalid phone number (must be a valid ${label} phone number)`;
}
/**
 * Builds a noun phrase listing the non-US countries in `allowedCountries`, suitable for an
 * error message like "send to {label} recipients". Derived from the allowlist so callers
 * don't hardcode country names that would go stale if the allowlist widens.
 * Examples: ['US', 'CA'] → "Canadian"; ['US', 'CA', 'GB'] → "Canadian or GB";
 * ['US', 'CA', 'GB', 'MX'] → "Canadian, GB, or MX".
 */
export function buildNonUsRecipientLabel(allowedCountries) {
    return joinCountryDisplayNames(allowedCountries.filter((code) => code !== 'US')) || 'non-US';
}
/**
 * Builds a noun phrase listing all `allowedCountries` using display names, suitable for an
 * error message like "must be a valid {label} phone number".
 * Examples: ['US'] → "US"; ['US', 'CA'] → "US or Canadian"; ['US', 'CA', 'GB'] → "US, Canadian, or GB".
 */
export function buildSupportedCountriesLabel(allowedCountries) {
    return joinCountryDisplayNames(allowedCountries);
}
function joinCountryDisplayNames(codes) {
    const displayNames = codes.map((code) => COUNTRY_CODE_DISPLAY_NAMES[code] ?? code);
    const lastDisplayName = displayNames.pop();
    if (!isDefined(lastDisplayName)) {
        return '';
    }
    if (displayNames.length === 0) {
        return lastDisplayName;
    }
    // Two names read "A or B"; three or more take the serial comma: "A, B, or C".
    const separator = displayNames.length === 1 ? ' or ' : ', or ';
    return `${displayNames.join(', ')}${separator}${lastDisplayName}`;
}
/**
 * Parses `value` as a phone number, treating the first entry of `allowedCountryCodes` as the
 * default region (used for bare national numbers without a `+` prefix). Returns the parsed number
 * iff it is valid AND its detected country is in `allowedCountryCodes`; otherwise returns
 * `undefined`.
 *
 * Note: national-format numbers (e.g. `4165551234` without `+`) are interpreted using only the
 * first allowed country as the default region. Within a shared calling code this is harmless —
 * `4165551234` under a `['US', 'CA']` allowlist still resolves to CA, because +1 area codes
 * identify the country on their own. Across calling codes it is not: a GB national number is
 * unparseable as US and gets rejected even when GB is allowed. Callers accepting national input
 * from more than one calling code must require international format.
 */
export function parseAllowedPhoneNumber(args) {
    const { value, allowedCountryCodes } = args;
    if (allowedCountryCodes.length === 0) {
        throw new Error('allowedCountryCodes must contain at least one country code');
    }
    const parsed = parsePhoneNumberFromString(value, allowedCountryCodes[0]);
    if (!parsed?.isValid() || !parsed.country || !allowedCountryCodes.includes(parsed.country)) {
        return undefined;
    }
    return parsed;
}
//# sourceMappingURL=countries.js.map